import { isNil } from 'rambda';
import { useDeferredValue, useEffect, useState } from 'react';

const VISIBILITY_THRESHOLD = 300;

export default function useVirtualKeyboardVisible(focusTargetSelector?: string) {
  const [isKeyboardVisible, setKeyboardVisible] = useState(false);
  const [isTargetFocused, setTargetFocused] = useState(false);
  const [windowHeight, setWindowHeight] = useState(0);
  const [visualViewportHeight, setVisualViewportHeight] = useState<number>();
  const [visualViewportOffsetTop, setVisualViewportOffsetTop] = useState<number>();
  const deferredVVHeight = useDeferredValue(visualViewportHeight);

  useEffect(() => {
    setWindowHeight(window.innerHeight);
    setVisualViewportHeight(window.visualViewport?.height ?? 0);
    setVisualViewportOffsetTop(window.visualViewport?.offsetTop ?? 0);
  }, []);

  useEffect(() => {
    if (isTargetFocused && !isNil(visualViewportHeight) && !isNil(visualViewportOffsetTop)) {
      setKeyboardVisible(visualViewportHeight < windowHeight || visualViewportOffsetTop > VISIBILITY_THRESHOLD);
    } else {
      setKeyboardVisible(false);
    }
  }, [isTargetFocused, visualViewportHeight, visualViewportOffsetTop, windowHeight]);

  useEffect(() => {
    const handleFocusIn = (e: FocusEvent) => {
      if (focusTargetSelector && !(e.target as HTMLElement).matches(focusTargetSelector)) return;
      setTargetFocused(true);
    };

    const handleFocusOut = (e: FocusEvent) => {
      if (focusTargetSelector && !(e.target as HTMLElement).matches(focusTargetSelector)) return;
      setTargetFocused(false);
    };

    const handleVisibilityChange = () => {
      document.visibilityState === 'hidden' && setTargetFocused(false);
    };

    const handleVisualViewportResize = () => {
      setVisualViewportHeight(window.visualViewport?.height);
      setVisualViewportOffsetTop(window.visualViewport?.offsetTop);
    };

    const handleWindowResize = () => {
      setWindowHeight(window.innerHeight);
    };

    document.body.addEventListener('focusin', handleFocusIn);
    document.body.addEventListener('focusout', handleFocusOut);
    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.visualViewport?.addEventListener('resize', handleVisualViewportResize);
    window.visualViewport?.addEventListener('scroll', handleVisualViewportResize);
    window.addEventListener('resize', handleWindowResize);

    return () => {
      document.body.removeEventListener('focusin', handleFocusIn);
      document.body.removeEventListener('focusout', handleFocusOut);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.visualViewport?.removeEventListener('resize', handleVisualViewportResize);
      window.visualViewport?.removeEventListener('scroll', handleVisualViewportResize);
      window.removeEventListener('resize', handleWindowResize);
    };
  }, [focusTargetSelector]);

  return {
    isKeyboardVisible,
    viewportHeight: deferredVVHeight,
  };
}
